import React from 'react';
import { useTheme } from '@material-ui/core/styles';
import Card from '@material-ui/core/Card';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import ListItemAvatar from '@material-ui/core/ListItemAvatar';
import Avatar from '@material-ui/core/Avatar';
import Grid from '@material-ui/core/Grid';

const pillars = [
  {
    icon: (
      <svg width={24} height={24} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
      </svg>
    ),
    title: 'Entrevista de autoridad (On-Site)',
    subtitle:
      'Visita programada al stand para entrevistar a sus voceros y grabar en campo lo que su equipo esté ejecutando en la feria: capacidades, soluciones y visión de mercado. (No implica presencia permanente durante todo el evento).',
  },
  {
    icon: (
      <svg width={24} height={24} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
      </svg>
    ),
    title: 'Cápsulas de innovación',
    subtitle:
      'Micro-videos de 60 segundos para destacar productos, servicios o soluciones específicas, optimizados para LinkedIn y entornos corporativos.',
  },
  {
    icon: (
      <svg width={24} height={24} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
      </svg>
    ),
    title: 'Validación audiovisual y proyección',
    subtitle:
      'Material con calidad cinematográfica TNO Network para que su empresa lo utilice como credencial de confianza en el entorno B2B.',
  },
];

const AboutSection = () => {
  const theme = useTheme();
  const isMd = useMediaQuery(theme.breakpoints.up('md'), {
    defaultMatches: true,
  });

  return (
    <Box>
      <Box marginBottom={{ xs: 4, sm: 8, md: 10 }}>
        <Typography
          sx={{ textTransform: 'uppercase', fontWeight: 600 }}
          gutterBottom
          color="primary"
          align="center"
        >
          Programa ancla
        </Typography>
        <Typography variant="h4" fontWeight={700} align="center" gutterBottom>
          TNO Business: la plataforma de referencia para el sector empresarial
        </Typography>
        <Typography
          variant="h6"
          component="p"
          color="textSecondary"
          align="center"
          maxWidth={780}
          margin="0 auto"
        >
          Al ser entrevistado bajo el sello de TNO Business, su marca adquiere una
          dimensión inmediata de autoridad. El programa disecciona visión
          corporativa, proyectos, innovación e impacto en la cadena de valor del
          petróleo y gas, conectando su ingeniería con el ecosistema de negocios
          de TNO Network.
        </Typography>
      </Box>
      <Grid container spacing={4} direction={isMd ? 'row' : 'column-reverse'}>
        <Grid item xs={12} md={6} data-aos={isMd ? 'fade-right' : 'fade-up'}>
          <Typography
            sx={{ textTransform: 'uppercase', fontWeight: 600 }}
            gutterBottom
            color="primary"
          >
            Pilares de la cobertura
          </Typography>
          <Typography variant="h5" fontWeight={700} gutterBottom>
            Ingeniería en el stand
          </Typography>
          <List disablePadding>
            {pillars.map((item, index) => (
              <ListItem
                key={index}
                disableGutters
                sx={{ alignItems: 'flex-start' }}
                data-aos="fade-up"
              >
                <ListItemAvatar>
                  <Box
                    component={Avatar}
                    variant="rounded"
                    bgcolor={`${theme.palette.primary.light}33`}
                    color={theme.palette.primary.dark}
                  >
                    {item.icon}
                  </Box>
                </ListItemAvatar>
                <ListItemText
                  primary={item.title}
                  secondary={item.subtitle}
                  primaryTypographyProps={{ fontWeight: 600 }}
                />
              </ListItem>
            ))}
          </List>
        </Grid>
        <Grid
          item
          xs={12}
          md={6}
          container
          justifyContent="center"
          alignItems="center"
          data-aos={isMd ? 'fade-left' : 'fade-up'}
        >
          <Box
            component={Card}
            boxShadow={4}
            borderRadius={2}
            overflow="hidden"
            sx={{
              bgcolor: theme.palette.common.white,
              border: 1,
              borderColor: 'divider',
            }}
          >
            <Box p={3}>
              <Typography variant="overline" color="primary" fontWeight={700}>
                Host corporativo
              </Typography>
              <Typography
                variant="body2"
                color="primary"
                fontWeight={600}
                sx={{ mb: 0.5 }}
              >
                La voz de los negocios
              </Typography>
              <Typography variant="h5" fontWeight={700} gutterBottom>
                Especialista TNO Business
              </Typography>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                Contamos con un ancla especializado en el sector empresarial que
                maneja el lenguaje técnico y corporativo que el sector petrolero
                exige. No es una entrevista de entretenimiento; es un diálogo de
                alto nivel sobre soluciones energéticas e impacto económico.
              </Typography>
              <Typography variant="body2" fontWeight={700} color="primary">
                Validación de marca, autoridad editorial y proyección de mercado
              </Typography>
              <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
                <Typography
                  variant="caption"
                  color="textSecondary"
                  display="block"
                  gutterBottom
                >
                  Programa ancla de la cobertura
                </Typography>
                <Box
                  sx={{
                    width: '100%',
                    position: 'relative',
                    height: 288,
                    mb: 1,
                  }}
                >
                  <img
                    src="/images/logos/logotnobusiness.png"
                    alt="TNO Business"
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: isMd ? 0 : -120,
                      backgroundColor: theme.palette.common.white,
                      borderRadius: 8,
                      padding: 20,
                      height: 288,
                      width: 'auto',
                      maxWidth: 1140,
                      objectFit: 'contain',
                      display: 'block',
                      marginRight: 'auto',
                    }}
                  />
                </Box>
                <Typography variant="h6" fontWeight={700} color="primary">
                  TNO Business
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  La plataforma empresarial de TNO Network funciona como sello de
                  credibilidad para convertir su participación en la Expo en una
                  pieza de posicionamiento corporativo de alto valor.
                </Typography>
              </Box>
            </Box>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default AboutSection;
